// Considered: AtlasPerspectiveSelector — rejected because it's sphere-scoped and has no "create new user by email" mode. See spec Reuse Audit.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import {
  InputAtom,
  SearchBarAtom,
  SegmentedControlAtom,
  SegmentedControlOption,
  SphereAvatar,
  useApiClientsOrNull,
} from 'design-system/my-lib';
import './OwnerPickerMolecule.scss';

export interface ExistingOwnerSelection {
  mode: 'existing';
  identityId: string;
  email: string | null;
  displayName: string | null;
}

export interface NewOwnerSelection {
  mode: 'new';
  email: string;
}

export type OwnerSelection = ExistingOwnerSelection | NewOwnerSelection;

export interface OwnerPickerMoleculeProps {
  value: OwnerSelection | null;
  onChange: (value: OwnerSelection | null) => void;
}

type OwnerTab = 'self' | 'existing' | 'new';

const SEARCH_DEBOUNCE_MS = 300;
const IDENTITY_SEARCH_LIMIT = 20;

interface IdentityResult {
  id: string;
  email: string | null;
  displayName: string | null;
  /**
   * Subject sphere id for this identity — the vertex that carries the
   * uploaded avatar image. Passed as SphereAvatar's sphereId so it can load
   * the image via globalSphereCache. Null when the identity has no active
   * subject sphere; SphereAvatar then falls back to initials.
   */
  subjectSphereId: string | null;
}

const OwnerPickerMolecule: React.FC<OwnerPickerMoleculeProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const deriveTab = (): OwnerTab => {
    if (!value) return 'self';
    return value.mode === 'existing' ? 'existing' : 'new';
  };

  const [tab, setTab] = useState<OwnerTab>(deriveTab);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<IdentityResult[]>([]);
  const [emailInput, setEmailInput] = useState(
    value?.mode === 'new' ? value.email : ''
  );

  const debounceHandle = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tabOptions: SegmentedControlOption<OwnerTab>[] = [
    { value: 'self', label: t('admin.organizations.addDialog.owner.tabSelf') },
    { value: 'existing', label: t('admin.organizations.addDialog.owner.tabExisting') },
    { value: 'new', label: t('admin.organizations.addDialog.owner.tabNew') },
  ];

  const handleTabChange = useCallback(
    (next: OwnerTab | OwnerTab[]) => {
      const nextTab = Array.isArray(next) ? next[0] : next;
      setTab(nextTab);
      setSearchInput('');
      setSearchQuery('');
      setResults([]);
      if (nextTab === 'self') {
        onChange(null);
      } else if (nextTab === 'existing') {
        onChange(null);
      } else {
        setEmailInput('');
        onChange(null);
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (debounceHandle.current) clearTimeout(debounceHandle.current);
    debounceHandle.current = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceHandle.current) clearTimeout(debounceHandle.current);
    };
  }, [searchInput]);

  useEffect(() => {
    if (tab !== 'existing' || !searchQuery || !apiClients?.graphClient) return;
    let cancelled = false;
    setIsSearching(true);
    (async () => {
      try {
        const response = await apiClients.graphClient.get('/admin/identities', {
          params: { search: searchQuery, limit: IDENTITY_SEARCH_LIMIT, offset: 0 },
        });
        if (!cancelled) setResults(response.identities ?? []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, searchQuery, apiClients]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const email = e.target.value;
      setEmailInput(email);
      if (email) {
        onChange({ mode: 'new', email });
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  const handleSelectIdentity = useCallback(
    (identity: IdentityResult) => {
      onChange({
        mode: 'existing',
        identityId: identity.id,
        email: identity.email,
        displayName: identity.displayName,
      });
    },
    [onChange]
  );

  return (
    <div className="owner-picker" data-testid="owner-picker">
      <SegmentedControlAtom<OwnerTab>
        options={tabOptions}
        value={tab}
        onChange={handleTabChange}
        mode="single"
        variant="strip"
        size="small"
        data-testid="owner-tab-control"
      />

      {tab === 'self' && (
        <p className="owner-picker__self-note" data-testid="self-note">
          {t('admin.organizations.addDialog.owner.selfNote')}
        </p>
      )}

      {tab === 'existing' && (
        <div className="owner-picker__existing" data-testid="existing-panel">
          <SearchBarAtom
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('admin.organizations.addDialog.owner.searchPlaceholder')}
            isSearching={isSearching}
          />
          {results.length > 0 && (
            <ul className="owner-picker__results" data-testid="identity-results">
              {results.map((identity) => {
                const initialsName =
                  identity.displayName || identity.email?.split('@')[0] || '';
                return (
                  <li key={identity.id} className="owner-picker__result">
                    <button
                      type="button"
                      className="owner-picker__result-item"
                      data-testid={`identity-result-${identity.id}`}
                      onClick={() => handleSelectIdentity(identity)}
                    >
                      <SphereAvatar
                        sphereId={identity.subjectSphereId ?? undefined}
                        sphereName={initialsName}
                        showInitials
                        size="small"
                        decorative
                        className="owner-picker__result-avatar"
                      />
                      <span className="owner-picker__result-text">
                        <span className="owner-picker__result-email">
                          {identity.email || '—'}
                        </span>
                        {identity.displayName && (
                          <span className="owner-picker__result-name">
                            {identity.displayName}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {value?.mode === 'existing' && (
            <p className="owner-picker__selected" data-testid="existing-selected">
              {t('admin.organizations.addDialog.owner.selected')}
            </p>
          )}
        </div>
      )}

      {tab === 'new' && (
        <div className="owner-picker__new" data-testid="new-panel">
          <InputAtom
            data-testid="email-input"
            type="email"
            value={emailInput}
            onChange={handleEmailChange}
            placeholder={t('admin.organizations.addDialog.owner.emailPlaceholder')}
            autoComplete="email"
          />
        </div>
      )}
    </div>
  );
};

export default OwnerPickerMolecule;
